import React, { useEffect, useState } from 'react';
import { Retry } from '@components/common';
import useDimension from '../hooks/useDimension';
import { TableWrapper, WidgetActions } from './styled';
import Search from './Search';
import { useSelector, useDispatch } from 'react-redux';
import { realTimeModeOnTrendsSelector } from '@store/common/selectors';
import { setDimensionTableHeaderInterval } from '@store/dimensionTables/actions';
import { useAtomValue } from 'jotai';
import { unitAtom } from '../atoms';
import Table from '@components/common/Table';
import useGrid from '../hooks/useGrid';
import { dimensionsMetricsToNASelector } from '@store/trends/base/selectors';
import VisualSwitch, { VisualSwitchProps } from './VisualSwitch';
import DimensionMap, { DimensionMapRef } from './DimensionMap';
import { shouldMapEnabled } from './utils';
import apptracker from '@utils/sensor/apptracker';
import { TOGGLE_TO_MAP_VIEW } from '@src/utils/sensor/track';
import { tableCompareEnabledSelector } from '@store/trends/birdChart/selectors';

function DimensionTable({ dimension, updateGrid }) {
  const [visualType, setVisualType] = useState<VisualSwitchProps['value']>('table');
  const dimensionMetricsToNA = useSelector(dimensionsMetricsToNASelector);
  const unit = useAtomValue(unitAtom);
  const realTimeOnTrends = useSelector(realTimeModeOnTrendsSelector);
  const isTableCompareEnabled = useSelector(tableCompareEnabledSelector);
  const dispatch = useDispatch();
  const {
    adjustedInterval,
    data,
    totals,
    columns,
    isFetching,
    isError,
    isEmpty,
    refetch,
    sortBy,
    onColumnSort,
    onSearch,
    searchText,
    pctShareMaxValues,
    metrics,
  } = useDimension({ dimension, dimensionMetricsToNA, unit });
  const mapRef = React.useRef<DimensionMapRef>(null);

  const handleVisualChange = (data: VisualSwitchProps['value']) => {
    setVisualType(data);
    if (data === 'map') {
      apptracker(TOGGLE_TO_MAP_VIEW, {
        dimension,
        metrics: metrics.join(','),
        unit,
      });
    }
  };

  useEffect(() => {
    if (realTimeOnTrends && adjustedInterval) {
      const [from, to] = adjustedInterval.split('/');
      dispatch(setDimensionTableHeaderInterval({ from, to }));
    }
  }, [realTimeOnTrends, adjustedInterval, dispatch]);

  useGrid({
    data,
    isError,
    isFetching,
    updateGrid,
    fixedHeight: visualType === 'map',
  });

  useEffect(() => {
    mapRef.current?.resetMapCenter();
  }, [searchText]);

  return (
    <>
      <WidgetActions>
        {shouldMapEnabled(dimension) && (
          <VisualSwitch value={visualType} onChange={handleVisualChange} />
        )}
        <Search onSearch={onSearch} text={searchText} />
      </WidgetActions>
      <TableWrapper>
        <Retry
          fetchData={refetch}
          isLoading={isFetching}
          showError={isError}
          // LoadingLayer={TableSkeleton}
          isEmpty={isEmpty}
          progress={true}
        >
          {visualType === 'table' ? (
            <Table
              fixed={true}
              data={data}
              frozenData={[totals]}
              columns={columns}
              headerHeight={35}
              rowHeight={35}
              rowKey={'value'}
              sortBy={sortBy}
              headerProps={{ pctShareMaxValues, showTrend: isTableCompareEnabled }}
              onColumnSort={onColumnSort}
            />
          ) : (
            <DimensionMap
              ref={mapRef}
              defaultMetric={sortBy.key}
              dimension={dimension}
              unit={unit}
              data={data}
            />
          )}
        </Retry>
      </TableWrapper>
    </>
  );
}

export default React.memo(DimensionTable);
